import { collection, doc, getDoc, getDocs, GeoPoint, query, where, limit } from "firebase/firestore";
import { firestore, GeoFirestore } from "../firebase";
import { distanceKm } from "./cities";

// Single fallback rule for every per-language field (aboutBusinessLang,
// locationTagsLang, tagsLang, menuListLang, ...): selected language, else
// English, else whatever else happens to exist. English is the hard-coded
// second choice on purpose — this must keep working exactly the same way
// even after 100 more languages get added, not fall back to "first key in
// the object" (object key order isn't something content editors control).
export function pickLang(langMap, langKey) {
  if (!langMap) return null;
  if (langMap[langKey] != null) return langMap[langKey];
  if (langMap.en_US != null) return langMap.en_US;
  const first = Object.values(langMap).find((v) => v != null);
  return first ?? null;
}

// Only restaurants/activities live in `lokasjoner`/`lokasjoner_list`, typed
// "restaurant"/"activity". Events are NOT a `lokasjoner` type at all — they
// live in their own `events` collection (see fetchEventsNear below), same
// as the app's locationLists.js. "studentforening" is a real `lokasjoner`
// type, but it's an unrelated feature (student organisations running their
// own Mapli business page — see myLocations.js) that a previous pass here
// mistook for events since it was the closest-sounding type available at
// the time.
export const CATEGORY_TYPE = {
  restaurants: "restaurant",
  activities: "activity",
};

// Same field names on `lokasjoner`, `lokasjoner_list` (leaner — no full
// menu/gallery), used for both the list query and the single-doc detail
// fetch. Missing fields on the lean doc just come back empty/null.
function mapLocationDoc(id, data) {
  return {
    id,
    name: data.locationName || "",
    slug: data.slug || "",
    type: data.type || "",
    secondaryType: data.secondaryType || "",
    // `tags`/`tagsLang` is the searchable filter-tag set (mapli app's
    // FilterScreen indexes tagsLang for its suggestions). `locationTags`/
    // `locationTagsLang` is a *separate* "main tags" set — the one actually
    // displayed on a location's card (see mapli app's locationCard.js). Do
    // not conflate the two.
    tags: Array.isArray(data.tags) ? data.tags : [],
    tagsLang: data.tagsLang || {},
    locationTags: Array.isArray(data.locationTags) ? data.locationTags : [],
    locationTagsLang: data.locationTagsLang || {},
    about: data.aboutBusiness || "",
    aboutLang: data.aboutBusinessLang || {},
    nativeLang: data.lang || "",
    address: data.adress || "",
    phone: data.phoneNumber || "",
    website: data.website || "",
    bookingLink: data.bookingLink || "",
    hours: data.regularOpeningHours || null,
    averagePrice: typeof data.averagePrice === "number" ? data.averagePrice : null,
    currency: data.currency || "NOK",
    overallRating: typeof data.overAllRating === "number" && data.overAllRating > 0 ? data.overAllRating : null,
    ratings: {
      google: { score: data.googleRating || 0, count: data.googleNumReviews || 0 },
      tripadvisor: { score: data.tripadvisorRating || 0, count: data.tripadvisorNumReviews || 0 },
      yelp: { score: data.yelpRating || 0, count: data.yelpNumReviews || 0 },
    },
    mainImage: data.mainProfileImage || "",
    gallery: Array.isArray(data.profileImages) ? data.profileImages.filter(Boolean) : [],
    menus: Array.isArray(data.menus) ? data.menus : null,
    menuListLang: data.menuListLang || null,
    menusChanged: typeof data.menusChanged === "number" ? data.menusChanged : null,
    allergyNote: data.allergiesSubstitutes?.text || "",
    lat: data.lat,
    lng: data.lng,
  };
}

// Fetches once per city (near() + active==true, the same proven pattern as
// getLocationsAdmin.js), then category tabs just filter this client-side —
// no refetch per tab. Uses lokasjoner_list (same field names as lokasjoner,
// but leaner — no full menu/gallery) since a results list doesn't need
// those; the profile view fetches the full lokasjoner doc on demand.
export async function fetchLocationsNear(city) {
  const geocollection = GeoFirestore.collection("lokasjoner_list");
  const geoQuery = geocollection
    .near({ center: new GeoPoint(city.lat, city.lng), radius: city.radiusKm })
    .where("active", "==", true);
  const snapshot = await geoQuery.get();
  return snapshot.docs.map((docSnap) => mapLocationDoc(docSnap.id, docSnap.data()));
}

// Same field names as a `lokasjoner` doc (name/tags/about/mainImage/lat/lng
// all map the same way — see mapLocationDoc), plus a few event-only fields
// on top. No `slug` field exists on events yet, so a place URL for one
// (buildPlacePath) gracefully falls back to the plain view instead of a
// pretty URL — fine for now, but worth knowing if event pages need SEO
// URLs later.
function mapEventDoc(id, data) {
  return {
    ...mapLocationDoc(id, data),
    eventDate: typeof data.eventDate === "number" && data.eventDate > 0 ? data.eventDate : null,
    endDate: typeof data.endDate === "number" && data.endDate > 0 ? data.endDate : null,
    eventTime: data.eventTime || "",
    aboutEvent: data.aboutEvent || "",
    aboutEventLang: data.aboutEventLang || {},
    eventOrganiser: data.eventOrganiser || "",
    eventOrganiserName: data.eventOrganiserName || "",
  };
}

// Real events live in their OWN `events` collection — not a `lokasjoner`
// type — same GeoFirestore near() + active-only pattern as
// fetchLocationsNear, and the same collection the app's locationLists.js
// queries (`geoEventcollection = GeoFirestore.collection('events')`).
//
// Past events are dropped client-side rather than with a Firestore
// `where("eventDate", ">=", ...)`: GeoFirestore's near() already applies its
// own range filter on the geohash field, and Firestore only allows an
// inequality filter on one field per query — adding a second one on
// eventDate would conflict with that.
export async function fetchEventsNear(city) {
  const geocollection = GeoFirestore.collection("events");
  const geoQuery = geocollection
    .near({ center: new GeoPoint(city.lat, city.lng), radius: city.radiusKm })
    .where("active", "==", true);
  const snapshot = await geoQuery.get();
  const startOfTodaySec = new Date(new Date().setHours(0, 0, 0, 0)).getTime() / 1000;
  return snapshot.docs
    .map((docSnap) => mapEventDoc(docSnap.id, docSnap.data()))
    .filter((e) => e.eventDate === null || e.eventDate >= startOfTodaySec);
}

// Full detail (menu, gallery, allergy note, etc.) for the place-profile
// view — the list-sourced object stays as a fallback while this loads.
export async function fetchLocationDetail(id) {
  const snap = await getDoc(doc(firestore, "lokasjoner", id));
  if (!snap.exists()) return null;
  return mapLocationDoc(snap.id, snap.data());
}

// A direct place URL (/lang/country/city/category/place) already tells us
// exactly which document we want — no need to wait for the whole city's
// location list (a multi-geohash-range near() query) to load first. One
// cheap indexed lookup by slug, and it returns the FULL doc already (same
// source as fetchLocationDetail), so the profile can open immediately.
export async function fetchLocationBySlug(slug) {
  const snap = await getDocs(query(collection(firestore, "lokasjoner"), where("slug", "==", slug), limit(1)));
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return mapLocationDoc(docSnap.id, docSnap.data());
}

export function withDistance(locations, city) {
  return locations.map((loc) => ({
    ...loc,
    distanceKm: typeof loc.lat === "number" && typeof loc.lng === "number" ? distanceKm(city.lat, city.lng, loc.lat, loc.lng) : null,
  }));
}

// No real price-tier field exists in the data (only a computed
// `averagePrice` number) — this buckets it into 3 tiers for the price
// facet. Locations with no averagePrice never get excluded by this facet.
export function priceTier(location) {
  if (!location.averagePrice) return null;
  if (location.averagePrice < 150) return "low";
  if (location.averagePrice < 300) return "mid";
  return "high";
}

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export function openStatus(hours, now = new Date()) {
  if (!hours) return null;
  const day = DAY_ORDER[(now.getDay() + 6) % 7]; // getDay(): 0=Sun -> map to mon-first order
  const today = hours[day];
  if (!today?.open || !today?.close) return { open: false };
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const openMin = today.open.hour * 60 + (today.open.minutes || 0);
  const closeMin = today.close.hour * 60 + (today.close.minutes || 0);
  const isOpen = minutesNow >= openMin && minutesNow < closeMin;
  return {
    open: isOpen,
    closesAt: isOpen ? `${String(today.close.hour).padStart(2, "0")}:${String(today.close.minutes || 0).padStart(2, "0")}` : null,
    opensAt: !isOpen ? `${String(today.open.hour).padStart(2, "0")}:${String(today.open.minutes || 0).padStart(2, "0")}` : null,
  };
}

export function formatHoursWeek(hours, dayLabels) {
  if (!hours) return [];
  return DAY_ORDER.map((day, i) => {
    const entry = hours[day];
    const value = entry?.open && entry?.close
      ? `${String(entry.open.hour).padStart(2, "0")}:${String(entry.open.minutes || 0).padStart(2, "0")} - ${String(entry.close.hour).padStart(2, "0")}:${String(entry.close.minutes || 0).padStart(2, "0")}`
      : dayLabels.closed;
    return { day: dayLabels.days[i], value };
  });
}

// Ported from the "Mapli Oversikt v2" design file's own script (the
// "På menyen" facet): a static curated vocabulary of menu-relevant words,
// matched against each location's own menu content, NOT a dynamic
// tokenizer and NOT tag-based — that's the actual designed behavior.
// eslint-disable-next-line max-len
// Curated "Type" vocabulary — cuisines + venue types only. locationTagsLang
// is free-form per-business input, so raw tag frequency always has a long
// tail of one-offs (rare cuisines, brand names like "McDonald's") mixed in
// with the common ones. Same fix pattern as MENU_VOCAB: a fixed, maintained
// list, intersected with whatever's actually present in the current
// results at display time (so it never shows an option with 0 matches).
export const TYPE_VOCAB = [
  "Restaurant", "Cafe", "Kafé", "Bar", "Pub", "Bistro", "Gastropub", "Steakhouse", "Fastfood", "Finedining", "Diner", "Brasserie", "Nattklubb",
  "Italiensk", "Meksikansk", "Japansk", "Kinesisk", "Thai", "Vietnamesisk", "Koreansk", "Indisk", "Pakistansk",
  "Amerikansk", "Fransk", "Spansk", "Skandinavisk", "Europeisk", "Middelhavet", "Marokkansk", "Argentinsk",
  "Libanesisk", "Tyrkisk", "Internasjonal", "Asiatisk", "Sushi", "Tapas", "Pizza", "Burger", "Sjømat",
];

export const MENU_VOCAB = ["burger", "cheeseburger", "vegansk burger", "halloumiburger", "lunsjburger", "blue cheese burger", "egonburger", "pizza", "margherita", "capricciosa", "pizzabuffet", "glutenfri pizza", "italiensk pizza", "januarpizza", "pasta", "carbonara", "arrabbiata", "lasagne", "risotto", "gnocchi", "ravioli", "tagliatelle", "spaghetti", "bolognese", "pesto", "tapas", "tapasbrett", "nachos", "chicken wings", "pommes frites", "søtpotetfries", "småretter", "deleretter", "salat", "caesarsalat", "kyllingsalat", "vegetarsalat", "suppe", "fiskesuppe", "sandwich", "club sandwich", "bagett", "wrap", "vegansk wrap", "focaccia", "surdeigsbrød", "kanelsnurr", "croissant", "kake", "sjokoladekake", "ostekake", "vaffel", "iskrem", "sorbet", "dessert", "sushi", "nigiri", "maki", "sashimi", "gyoza", "karaage", "ramen", "nudler", "pad thai", "bao buns", "curry", "vegansk curry", "tofu", "tofu bao", "biff", "entrecôte", "ribeye", "svinekam", "kylling", "kyllingfilet", "lam", "reinsdyr", "elg", "torsk", "skrei", "laks", "ørret", "sei", "kveite", "reker", "krabbe", "kamskjell", "blåskjell", "fish and chips", "fiskekaker", "kebab", "shawarma", "taco", "burrito", "quesadilla", "dumplings", "springrolls", "pho", "bibimbap", "bulgogi", "frokost", "brunsj", "egg benedict", "eggerøre", "bacon", "pannekaker", "grøt", "yoghurt", "granola", "müsli", "kaffe", "filterkaffe", "espresso", "americano", "cappuccino", "latte", "flat white", "cortado", "chai latte", "varm sjokolade", "te", "smoothie", "milkshake", "brus", "fanta", "cola", "iste", "thai iste", "juice", "appelsinjuice", "øl", "ipa", "lokal ipa", "pils", "lager", "stout", "weissbier", "cider", "vin", "hvitvin", "rødvin", "rosé", "musserende", "champagne", "prosecco", "sake", "cocktail", "negroni", "mojito", "alkoholfri mojito", "espresso martini", "gin tonic", "aperol spritz", "yuzu spritz", "whisky", "rom", "vegetar", "vegansk", "glutenfri", "laktosefri", "nøttefri", "barnemeny", "forrett", "hovedrett", "buffet", "lunsj", "lunsjmeny", "dagens rett", "take away", "catering", "melk", "gluten", "nøtter", "peanøtter", "soya", "sesam", "sennep", "selleri", "sulfitt", "fisk", "skalldyr"];

// Word-boundary substring match — "burger" matches "...en burger med..."
// but not "hamburgerbrød". Ported verbatim from the design file.
function hasTerm(haystack, term) {
  const t = term.toLowerCase();
  let i = haystack.indexOf(t);
  while (i !== -1) {
    const before = i === 0 ? " " : haystack.charAt(i - 1);
    const after = i + t.length >= haystack.length ? " " : haystack.charAt(i + t.length);
    const wordish = /[a-zà-ÿ0-9]/i;
    if (!wordish.test(before) && !wordish.test(after)) return true;
    i = haystack.indexOf(t, i + 1);
  }
  return false;
}

// tags + description + menu category names + item names/notes, all
// lowercased into one searchable string — same composition as the design.
// The design file cached this on the object itself (`p._hay`) precisely
// because it gets called once per MENU_VOCAB word per location (~180 times
// each) while building suggestions — without caching, that's ~180x the
// string-building work per location, every keystroke. Same fix here: build
// it once per location+language, cache on the object, reuse after that.
export function menuHaystack(location, langKey) {
  if (!location._hay) location._hay = {};
  if (location._hay[langKey]) return location._hay[langKey];

  const tags = pickLang(location.tagsLang, langKey) || [];
  const locationTags = pickLang(location.locationTagsLang, langKey) || [];
  const about = pickLang(location.aboutLang, langKey) || location.about || "";
  let s = tags.join(" ") + " " + locationTags.join(" ") + " " + about;
  const cats = location.menus || pickLang(location.menuListLang, langKey) || [];
  cats.forEach((cat) => {
    s += " " + (cat.category || "");
    (cat.menuItems || []).forEach((name, i) => {
      s += " " + name + " " + (cat.descriptions?.[i] || "");
    });
  });
  location._hay[langKey] = s.toLowerCase();
  return location._hay[langKey];
}

const MENU_WORD_STOPWORDS = new Set([
  // Norwegian
  "og", "med", "en", "et", "den", "det", "som", "på", "av", "for", "til", "er", "har", "om", "fra", "de", "du",
  "vi", "han", "hun", "eller", "men", "så", "ikke", "kan", "vil", "skal", "blir", "var", "ble", "også", "hos",
  "hver", "alle", "noen", "litt", "liten", "stor", "her", "der", "vår", "din",
  // English
  "and", "with", "the", "a", "an", "of", "for", "to", "is", "on", "in", "or", "but", "not", "your", "our",
  "some", "all", "from", "our",
]);

// Individual words pulled from just the menu ITEM NAMES (not the longer,
// noisier descriptions) for one location — lowercased, short words and
// stopwords dropped, deduplicated. Powers "search every word on every
// menu in the city", not just the curated MENU_VOCAB list: that list is
// intentionally capped at ~180 common terms (see its comment above), but a
// single city's real menus have hundreds more specific dishes/ingredients
// that still need to be findable. Cached per location+language, same
// reasoning as menuHaystack.
export function menuItemWords(location, langKey) {
  if (!location._menuWords) location._menuWords = {};
  if (location._menuWords[langKey]) return location._menuWords[langKey];

  const words = new Set();
  const addWords = (text) => {
    (text || "")
      .toLowerCase()
      .split(/[^a-zà-ÿ0-9]+/)
      .forEach((w) => {
        if (w.length >= 3 && !MENU_WORD_STOPWORDS.has(w)) words.add(w);
      });
  };

  const cats = location.menus || pickLang(location.menuListLang, langKey) || [];
  cats.forEach((cat) => (cat.menuItems || []).forEach(addWords));

  // A word like "tørrfisk" is often a tag, not a literal menu item name —
  // menuHaystack (the actual filter match) already includes both tag sets,
  // so discovery needs to as well, or a real, filterable word never shows
  // up as something to search for in the first place.
  (pickLang(location.tagsLang, langKey) || []).forEach(addWords);
  (pickLang(location.locationTagsLang, langKey) || []).forEach(addWords);

  location._menuWords[langKey] = words;
  return words;
}

export function servesAllTerms(location, terms, langKey) {
  const hay = menuHaystack(location, langKey);
  return terms.every((term) => hasTerm(hay, term));
}

export function menuTermCount(locations, term, langKey) {
  return locations.filter((l) => hasTerm(menuHaystack(l, langKey), term)).length;
}

// Which of `terms` this specific location's menu actually contains — for
// the "På menyen: [word]" row on each result card.
export function matchedMenuTerms(location, terms, langKey) {
  const hay = menuHaystack(location, langKey);
  return terms.filter((term) => hasTerm(hay, term));
}

// React crashes ("Objects are not valid as a React child") if a raw field
// turns out to be an object/Timestamp instead of a string/number — this is
// the safety net so a review can never carry one through to render, no
// matter what shape a source's API happens to send.
function reviewString(value) {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
}

// Google and Tripadvisor's raw review shapes are genuinely different, not
// just differently-named — Tripadvisor's "author" is nested at
// review.user.username (review.user itself is an object with avatar/
// location info, not a name), and its date is an ISO string
// (published_date), not the unix-seconds `time` Google uses. Each source is
// unpacked on its own terms instead of one generic fallback chain across
// incompatible shapes.
function normalizeReview(sourceKey, r) {
  let author = "";
  let date = "";
  let avatarUrl = "";
  let title = "";
  let visitedRaw = ""; // ISO-ish date the reviewer visited, source-specific — not every source has one
  if (sourceKey === "tripadvisor") {
    author = reviewString(r.user?.username);
    date = reviewString(r.published_date || r.travel_date);
    avatarUrl = reviewString(r.user?.avatar?.medium || r.user?.avatar?.large || r.user?.avatar?.thumbnail);
    title = reviewString(r.title);
    visitedRaw = reviewString(r.travel_date);
  } else if (sourceKey === "google") {
    author = reviewString(r.author_name);
    date = reviewString(r.relative_time_description) || (typeof r.time === "number" ? new Date(r.time * 1000).toLocaleDateString() : "");
    avatarUrl = reviewString(r.profile_photo_url);
  } else {
    // Yelp (or anything else): best effort, still routed through reviewString.
    author = reviewString(r.author_name) || reviewString(r.author);
    date = reviewString(r.date) || reviewString(r.time);
  }
  return {
    source: sourceKey,
    author,
    avatarUrl,
    title,
    visitedRaw,
    rating: typeof r.rating === "number" ? r.rating : typeof r.score === "number" ? r.score : null,
    text: reviewString(r.text || r.body || r.comment),
    date,
  };
}

// Reviews are never a normal field — each source's actual written reviews
// live in a subcollection under the location, one doc per historical fetch.
export async function fetchReviewsFor(locationId) {
  const sources = [
    { key: "tripadvisor", path: "tripAdvisorRatings" },
    { key: "yelp", path: "yelpRatings" },
    { key: "google", path: "googleRating" },
  ];
  const results = await Promise.all(
    sources.map(async ({ key, path }) => {
      try {
        const snap = await getDocs(collection(firestore, "lokasjoner", locationId, path));
        const reviews = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (Array.isArray(data.reviews)) reviews.push(...data.reviews);
        });
        return reviews.map((r) => normalizeReview(key, r));
      } catch {
        return [];
      }
    }),
  );
  return results.flat();
}
